/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';

import { combineLatest, Observable, of, timer } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, switchMapTo, take, tap, withLatestFrom } from 'rxjs/operators';

import { RestService } from '../../../modules/shared/services/rest.service';
import {
    AuthCredentials,
    AuthStateModel,
    LoginResponse,
    TokenRefreshPayload,
    TokenRefreshResponse,
    UserInfoResponse
} from '../state/authState.model';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { AuthState } from '../state/authState.state';
import { Actions, ofActionSuccessful, Select, Store } from '@ngxs/store';
import {
    BeginLogout,
    BeginPasswordLogin,
    BeginTokenRefresh,
    EndInitialAuthentication,
    LoggedOut,
    LoginFailed,
    LoginSuccessful,
    NavigateToRequestedRoute,
    RedirectToLogin,
    RequestUserInfo,
    SetUserInfo,
    TokenRefreshFailed,
    TokenRefreshSuccessful
} from '../state/authState.actions';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { $D } from 'rxjs-debug';
import { ThrowError } from '../../state/notification/notification.actions';

@Injectable()
export class LoginService {

    @Select(AuthState.token) token$: Observable<string>;
    @Select(AuthState.refreshToken) refreshToken$: Observable<string>;

    constructor(
        private logger: LoggerService,
        private rest: RestService,
        private router: Router,
        private oauth: OAuthService,
        private jwtService: JwtHelperService,
        private store: Store,
        private actions$: Actions
    ) {
        const authConfigLoaded$ = this.store.select(AuthState.loaded).pipe(filter(Boolean));
        const refreshToken$ = this.store.select(AuthState.refreshToken);
        const activeMethod$ = this.store.select(AuthState.activeMethod);
        const excludedRoutes$ = this.store.select(AuthState.excludedRoutes);
        const inProgress$ = this.store.select(AuthState.authenticationInProgress);

        // don't authenticate if setting password
        const isRouteNonAuthenticated$ = excludedRoutes$.pipe(
            map((routes: string[]) => {
                const currentPath = this.router.url.split(/[#?]/)[0];
                return routes.includes(currentPath);
            })
        );

        // check initial auth state
        authConfigLoaded$.pipe(
            switchMapTo(
                combineLatest([
                    refreshToken$,
                    activeMethod$,
                    isRouteNonAuthenticated$
                ]).pipe(
                    take(1)
                )
            ),
            filter(([ , activeMethod ]) => [ 'password', 'none' ].includes(activeMethod)),
            filter(([ , , excludeRoute ]) => !excludeRoute),

            map(([ refreshToken ]) => refreshToken),

            tap((refreshToken) => {
                const token = this.store.selectSnapshot(AuthState.token);

                // end auth if:
                //  there's no token and no refresh token
                if (!token && !refreshToken) {
                    return;
                }

                // refresh token if:
                //  there's a refresh token and the no token or token will expire in <= 2 minutes
                if (refreshToken && (!token || this.jwtService.isTokenExpired(token, 120))) {
                    setTimeout(() => this.store.dispatch(new BeginTokenRefresh()), 0);
                    return;
                }
                // validate (via GetUserInfo) if:
                //  the token exists and is not expired
                if (token && !this.jwtService.isTokenExpired(token)) {
                    setTimeout(() => this.store.dispatch(new RequestUserInfo()), 0);
                }
            }),
            tap(() => setTimeout(() => this.store.dispatch(new EndInitialAuthentication()), 0)),
        ).subscribe();

        const trigger$ = timer(10000, 10000).pipe(map(() => true));

        authConfigLoaded$.pipe(
            // initiate each 10sec tick
            switchMapTo(trigger$),

            switchMapTo(
                combineLatest([
                    refreshToken$,
                    activeMethod$,
                    isRouteNonAuthenticated$
                ]).pipe(
                    take(1)
                )
            ),

            filter(([ , activeMethod ]) => activeMethod === 'password'),
            filter(([ , , excludeRoute ]) => !excludeRoute),

            map(([ refreshToken ]) => refreshToken),

            tap((refreshToken) => {
                const token = this.store.selectSnapshot(AuthState.token);

                // end auth if:
                //  there's no token and no refresh token
                if (!token && !refreshToken) {
                    setTimeout(() => this.store.dispatch(new RedirectToLogin(null)), 0);
                    return;
                }

                // refresh token if:
                //  there's a refresh token and the no token or token will expire in <= 2 minutes
                if (refreshToken && (!token || this.jwtService.isTokenExpired(token, 120))) {
                    setTimeout(() => this.store.dispatch(new BeginTokenRefresh()), 0);
                    return;
                }
            })
        ).subscribe();

        this.actions$.pipe(
            ofActionSuccessful(BeginPasswordLogin),
            concatMap((action) => this.login(action.payload.credentials).pipe(take(1)))
        ).subscribe((result: LoginResponse) => {
            if (result.success) {
                this.store.dispatch(new LoginSuccessful({
                    user: result.user,
                    token: result.token,
                    refreshToken: result.refreshToken,
                }));
            } else {
                this.store.dispatch(new LoginFailed({ message: 'Login failed' }));
            }
        });

        this.actions$.pipe(
            ofActionSuccessful(BeginLogout),
            withLatestFrom(this.refreshToken$),
            tap(([ , refreshToken ]) => this.logout(refreshToken)),
            tap(() => this.oauth.logOut(true)),
            tap(([ action ]) => setTimeout(() => this.store.dispatch(new LoggedOut(action.redirect)), 0))
        ).subscribe();

        this.actions$.pipe(
            ofActionSuccessful(RequestUserInfo),
            withLatestFrom(this.token$),
            concatMap(([ , token ]) => this.getUserInfo(token))
        ).subscribe(
            (response) => setTimeout(() => this.store.dispatch(new SetUserInfo(response)), 0)
        );

        this.actions$.pipe(
            ofActionSuccessful(BeginTokenRefresh),
            withLatestFrom(this.refreshToken$),
            switchMap(([ , refreshToken ]) => this.tokenRefresh({ refreshToken })),
            tap((response) => setTimeout(() => this.store.dispatch(
                response.success
                    ? new TokenRefreshSuccessful(response)
                    : new TokenRefreshFailed(response.message)
            ), 0)),
            catchError((err, caught$) => {
                return this.store.dispatch(new ThrowError(err));
            }),
        ).subscribe();

        this.actions$.pipe(
            ofActionSuccessful(TokenRefreshFailed),
            tap(() => setTimeout(() => this.store.dispatch(new RedirectToLogin(null)), 0))
        ).subscribe();

        this.actions$.pipe(
            ofActionSuccessful(TokenRefreshSuccessful),
            tap(() => setTimeout(() => this.store.dispatch(new RequestUserInfo()), 0))
        ).subscribe();

        this.actions$.pipe(
            ofActionSuccessful(SetUserInfo),
            tap(() => setTimeout(() => this.store.dispatch(new NavigateToRequestedRoute()), 0))
        ).subscribe();
    }

    public login(credentials: AuthCredentials): Observable<Partial<AuthStateModel>> {
        const loginParams = {
            username: credentials.username,
            password: credentials.password
        };
        return this.rest.apiPost<LoginResponse>('auth/login', loginParams).pipe(
            catchError((err) => {
                this.logger.error(err);
                return of<LoginResponse>({
                    user: {
                        id: -1,
                        username: '',
                        roles: [] as string[]
                    },
                    token: null,
                    refreshToken: null
                });
            })
        );
    }

    public getUserInfo(token = ''): Observable<UserInfoResponse> {
        const options = {
            token: token || this.oauth.getIdToken()
        };
        return this.rest.apiPost<UserInfoResponse>('auth/userinfo', options);
    }

    public tokenRefresh(options: TokenRefreshPayload): Observable<TokenRefreshResponse> {
        return this.rest.apiPost<TokenRefreshResponse>('auth/refresh', options, {}, {skipErrorHandler: true});
    }

    public logout(token: string) {
        return this.rest.apiPost<void>('auth/logout', { refreshToken: token });
    }

    // private checkForRefresh(input: Observable<any>): Observable<boolean> {
    //
    // }
}
